import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";

const UsageBilling = () => {
    const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <div className="min-h-screen bg-background font-sans">
            <SEOHead
                title="Usage & Billing Policy | Frayze Technologies"
                description="Learn about Frayze's transparent usage-based billing, monthly credits, and rates for communication, AI, and automation services."
                keywords="usage billing, frayze billing policy, ai credits, communication rates, automation fees"
                canonical="https://frayze.ca/usage-billing"
                robots="noindex, follow"
            />
            <Header />

            <main className="pt-40 pb-20 px-6">
                <div className="max-w-4xl mx-auto prose prose-slate dark:prose-invert">
                    <h1>Frayze Usage & Billing Policy</h1>
                    <p className="text-muted-foreground">Effective Date: {currentDate}</p>

                    <h2>1. Overview</h2>
                    <p>
                        Frayze Technologies Inc. (“Frayze,” “we,” “our”) uses a hybrid pricing model consisting of:
                    </p>
                    <ul>
                        <li>Monthly subscription fees based on your chosen plan.</li>
                        <li>Monthly usage credits included with each plan.</li>
                        <li>Usage-based billing for communication, AI, and automation activity that exceeds included credits.</li>
                    </ul>
                    <p>
                        Usage billing ensures customers only pay for what they use and supports transparent, cost-effective scaling as lead volume and activity increase.
                    </p>
                    <p>
                        This Usage & Billing Policy explains how usage is measured, billed, and credited.
                    </p>
                    <p>
                        <strong>Note:</strong> Subscription and usage fees cover access to the Frayze platform and services. All Creative Work Product (websites, designs, code, copy and creative assets) produced by Frayze Technologies Inc. remains the exclusive intellectual property of Frayze. Payments do not constitute a purchase or transfer of ownership of any Creative Work Product. See our Terms of Service for full details.
                    </p>

                    <h2>2. What Counts as Usage</h2>
                    <p>Usage is divided into three categories:</p>

                    <h3>A. Communication Usage</h3>
                    <p>Includes all communication events processed through your Frayze account:</p>
                    <ul>
                        <li>Incoming and outgoing SMS</li>
                        <li>Incoming and outgoing MMS</li>
                        <li>Phone calls (inbound and outbound)</li>
                        <li>Voicemail drops</li>
                        <li>Email sending</li>
                        <li>Toll-free and international rates (if applicable)</li>
                    </ul>
                    <p>
                        Communication usage relies on underlying third-party providers (e.g., Twilio, Mailgun, SendGrid, Telnyx). Rates vary by region and carrier and are passed through at standard wholesale cost unless otherwise noted.
                    </p>

                    <h3>B. AI Usage</h3>
                    <p>AI usage applies to any artificial intelligence processes within your account, including:</p>
                    <ul>
                        <li>AI message responders</li>
                        <li>AI email generators</li>
                        <li>AI chat assistants</li>
                        <li>AI voice agents (per minute)</li>
                        <li>AI task processors (per action)</li>
                        <li>AI workflow triggers</li>
                        <li>Content, ad, or post generation</li>
                    </ul>
                    <p>
                        AI voice minutes are billed per minute of conversation.<br />
                        AI text interactions are billed per processing request.
                    </p>
                    <p>
                        AI usage may include Frayze markup when delivering specialized or industry-specific AI agents.
                    </p>

                    <h3>C. Automation Usage</h3>
                    <p>Automations include:</p>
                    <ul>
                        <li>Workflow triggers</li>
                        <li>Conditional logic steps</li>
                        <li>API calls</li>
                        <li>Webhooks</li>
                        <li>Event listeners</li>
                        <li>CRM object creation</li>
                        <li>Status updates and automations inside pipelines</li>
                    </ul>
                    <p>
                        Automation usage is billed when workflows execute.<br />
                        High-volume automations may incur additional platform charges.
                    </p>

                    <h2>3. Monthly Usage Credits</h2>
                    <p>Each subscription tier includes monthly usage credits:</p>
                    <div className="not-prose overflow-x-auto">
                        <table className="w-full border-collapse text-left text-sm">
                            <thead>
                                <tr className="border-b">
                                    <th className="py-2 font-semibold">Plan</th>
                                    <th className="py-2 font-semibold">Usage Credits</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b">
                                    <td className="py-2">CRM Lite</td>
                                    <td className="py-2">$10</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2">CRM Foundation</td>
                                    <td className="py-2">$50</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2">Growth Engine</td>
                                    <td className="py-2">$100</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2">Complete Growth System</td>
                                    <td className="py-2">$250</td>
                                </tr>
                                <tr>
                                    <td className="py-2">Enterprise</td>
                                    <td className="py-2">$500–$1,000+</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-4">Credits apply to:</p>
                    <ul>
                        <li>Calls</li>
                        <li>Texts</li>
                        <li>Emails</li>
                        <li>AI usage</li>
                        <li>Automation usage</li>
                    </ul>
                    <p>
                        Credits are used automatically at the start of each billing cycle and do not roll over.
                    </p>

                    <h2>4. Usage Rates</h2>
                    <p>
                        The table below shows current approximate rates and a practical sense of what $10 in usage credit actually buys you.
                        Rates are based on current provider costs (e.g. Twilio, Mailgun, OpenAI GPT-4.1 equivalents) and may vary slightly by region or model.
                        You only pay for what you use. No surprises.
                    </p>
                    <div className="not-prose overflow-x-auto">
                        <table className="w-full border-collapse text-left text-sm">
                            <thead>
                                <tr className="border-b bg-muted/40">
                                    <th className="py-3 px-3 font-semibold">Category</th>
                                    <th className="py-3 px-3 font-semibold">Rate</th>
                                    <th className="py-3 px-3 font-semibold">$10 Gets You Approximately</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b">
                                    <td className="py-2.5 px-3">Outbound Calls</td>
                                    <td className="py-2.5 px-3">$0.028 / min</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~355 minutes</td>
                                </tr>
                                <tr className="border-b bg-muted/20">
                                    <td className="py-2.5 px-3">Inbound Calls</td>
                                    <td className="py-2.5 px-3">$0.017 / min</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~590 minutes</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2.5 px-3">Text Messages (SMS)</td>
                                    <td className="py-2.5 px-3">$0.0166 / segment</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~600 segments</td>
                                </tr>
                                <tr className="border-b bg-muted/20">
                                    <td className="py-2.5 px-3">Emails</td>
                                    <td className="py-2.5 px-3">$0.00135 / email</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~7,405 emails</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2.5 px-3">Email Verification</td>
                                    <td className="py-2.5 px-3">$0.005 / verification</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~2,000 verifications</td>
                                </tr>
                                <tr className="border-b bg-muted/20">
                                    <td className="py-2.5 px-3">Content AI (Words)</td>
                                    <td className="py-2.5 px-3">$0.18 / 1,000 words</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~55,556 words</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2.5 px-3">Content AI (Images)</td>
                                    <td className="py-2.5 px-3">$0.12 / image</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~85 images</td>
                                </tr>
                                <tr className="border-b bg-muted/20">
                                    <td className="py-2.5 px-3">Conversation & Voice AI</td>
                                    <td className="py-2.5 px-3">$0.03 / message · $0.175 / voice min</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~335 messages or ~57 voice mins</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2.5 px-3">Reviews AI Responses</td>
                                    <td className="py-2.5 px-3">$0.03 / response</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~335 responses</td>
                                </tr>
                                <tr className="border-b bg-muted/20">
                                    <td className="py-2.5 px-3">Funnel AI / Basic Websites</td>
                                    <td className="py-2.5 px-3">$1.98 / funnel</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">~5 funnels</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2.5 px-3">Local Phone Number</td>
                                    <td className="py-2.5 px-3">~$2.00 / month</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">-</td>
                                </tr>
                                <tr>
                                    <td className="py-2.5 px-3">Toll-Free Phone Number</td>
                                    <td className="py-2.5 px-3">~$3.00 / month</td>
                                    <td className="py-2.5 px-3 text-muted-foreground">-</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-4 text-sm text-muted-foreground">
                        Rates are approximate estimates based on current provider costs. Actual costs may vary slightly by model, region, or carrier.
                        Exact usage is always visible in your Frayze dashboard. Frayze passes communication costs through at wholesale unless otherwise noted.
                    </p>

                    <h2>5. Billing Cycles</h2>
                    <ul>
                        <li>Usage is billed monthly on the same date as your subscription renewal.</li>
                        <li>Any usage beyond included credits is added automatically to your next invoice.</li>
                        <li>Charges may appear as “usage fees,” “AI usage,” “communication usage,” or similar descriptors.</li>
                    </ul>

                    <h2>6. Notifications & Monitoring</h2>
                    <p>You can monitor usage inside your Frayze dashboard.</p>
                    <p>Frayze may send optional alerts when:</p>
                    <ul>
                        <li>Usage credits are 75% consumed</li>
                        <li>Usage credits are fully consumed</li>
                        <li>Unusual spikes or suspicious usage patterns are detected</li>
                    </ul>
                    <p>Clients are responsible for monitoring ongoing usage.</p>

                    <h2>7. No Refunds on Usage Charges</h2>
                    <p>All usage fees are non-refundable, including:</p>
                    <ul>
                        <li>SMS/MMS messages</li>
                        <li>Calls</li>
                        <li>AI agent time</li>
                        <li>Emails</li>
                        <li>Automation events</li>
                    </ul>
                    <p>Even if messages go unanswered or calls are not picked up, usage still applies.</p>

                    <h2>8. Fraud & Abuse Prevention</h2>
                    <p>Frayze reserves the right to:</p>
                    <ul>
                        <li>Pause usage</li>
                        <li>Suspend accounts</li>
                        <li>Disable features</li>
                        <li>Enforce rate limits</li>
                    </ul>
                    <p>
                        …if usage indicates spam, violation of Acceptable Use, or risk to platform integrity.
                    </p>

                    <h2>9. Changes to Usage & Billing</h2>
                    <p>
                        Frayze may revise usage rates or credit amounts due to vendor pricing changes or platform updates.<br />
                        Any changes will be communicated via email or dashboard notice.
                    </p>

                    <h2>10. Contact</h2>
                    <p>If you have questions about usage billing:</p>
                    <p>
                        Email: <a href="mailto:billing@frayze.ca" className="text-primary hover:underline">billing@frayze.ca</a><br />
                        Phone: <a href="tel:+18077000079" className="text-primary hover:underline">+1 807-700-0079</a>
                    </p>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default UsageBilling;
